// Pure math for the Experiment page's stress-profile progress timeline -
// turns a run's planned steps + the controller's own reported step_index
// into "where is this run right now" (a phase, per-segment fill, overall
// %, overrun/staleness flags). No React, no fetching, no timers - the
// caller passes `nowMs` explicitly (Date.now()) so tests stay deterministic
// without fake timers, matching this codebase's existing convention.
//
// Firmware background (auto-greenhouse-firmware/src/stress_scheduler.cpp,
// confirmed by direct code read): a step advances purely on elapsed
// wall-clock time reaching its planned duration_seconds - never gated on
// whether temp/RH actually reached target. So "elapsed since step
// started / planned duration" is a legitimate progress metric, it just has
// to handle real overrun gracefully (a run sat stuck on one step for ~2
// hours against a ~10-minute plan during this project's own testing) and
// real staleness (the controller can just stop reporting) without looking
// broken.

// 2 minutes - about 24 missed 5-second telemetry reports while a run is
// live. Past this, treat the last known position as frozen rather than
// letting elapsed-time math run away unbounded.
const STALE_AFTER_MS = 2 * 60 * 1000;

// Overrun grace: accounts for the firmware's own small clock drift (its
// tick loop drops sub-tick milliseconds when blocked) and NVS-resume
// rounding after a reboot - not just network/telemetry jitter.
const MIN_GRACE_SECONDS = 60;
const GRACE_FRACTION = 0.1;

function stepDurationSeconds(step) {
  const value = Number(step?.duration_seconds);
  return Number.isFinite(value) && value > 0 ? value : 0;
}

function parseMs(iso) {
  if (!iso) return null;
  const ms = Date.parse(iso);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * @param {object} args
 * @param {Array}  args.steps - planned steps ({kind, temp_c/to_temp_c,
 *   rh_pct/to_rh_pct, duration_seconds}), e.g. GreenhouseRun's `steps`
 *   field from greenhouse_devices.
 * @param {object|null} args.progress - {step_index, step_state,
 *   step_started_at, last_reported_at} or null (no telemetry yet).
 * @param {boolean} args.isActive - true for active_run, false for a
 *   finished last_run.
 * @param {boolean} [args.stoppedByOperator] - last_run only; true when a
 *   stop_run command exists for this run.
 * @param {boolean} [args.startRejected] - active_run only; true when the
 *   start command's latest_command was acked with a non-ok status.
 * @param {number}  [args.nowMs] - defaults to Date.now().
 *
 * @returns {{phase: string, segments: Array, overallPct: number|null,
 *   overrunSeconds?: number, isOverrun?: boolean, isStale?: boolean,
 *   remainingSeconds?: number, remainingIsLowerBound?: boolean,
 *   currentStepIndex?: number, stepElapsedSeconds?: number,
 *   stepPlannedSeconds?: number, stoppedByOperator?: boolean}}
 *   `overallPct` is an unrounded 0-100 float (or null when not
 *   meaningful) - round for display at the call site, not here.
 */
export function computeRunProgress({
  steps,
  progress,
  isActive,
  stoppedByOperator = false,
  startRejected = false,
  nowMs = Date.now(),
}) {
  const safeSteps = Array.isArray(steps) ? steps : [];
  const n = safeSteps.length;

  if (n === 0) {
    return { phase: "no-plan", segments: [], overallPct: null };
  }

  const weights = safeSteps.map(stepDurationSeconds);
  const totalPlanned = weights.reduce((a, b) => a + b, 0);
  const baseSegments = safeSteps.map((step, index) => ({
    index,
    step,
    weight: weights[index],
    status: "upcoming",
    fillPct: 0,
  }));

  if (isActive && startRejected) {
    return { phase: "rejected", segments: baseSegments, overallPct: null };
  }
  if (isActive && !progress) {
    return { phase: "waiting", segments: baseSegments, overallPct: null };
  }
  if (!isActive && !progress) {
    return { phase: "ended-no-data", segments: baseSegments, overallPct: null };
  }

  const k = progress.step_index;
  const validIndex = typeof k === "number" && Number.isFinite(k) && k >= 0 && k < n;
  if (!validIndex) {
    return { phase: "unknown-position", segments: baseSegments, overallPct: null };
  }

  if (isActive && progress.step_state === "GOING_SAFE") {
    return {
      phase: "finishing",
      segments: baseSegments.map((s, i) => ({
        ...s,
        status: i < k ? "done" : i === k ? "current" : "upcoming",
        fillPct: i < k ? 100 : 0,
      })),
      overallPct: null,
      currentStepIndex: k,
    };
  }

  const lastReportedAtMs = parseMs(progress.last_reported_at);
  const stepStartedAtMs = parseMs(progress.step_started_at) ?? lastReportedAtMs;
  const isStale =
    isActive && lastReportedAtMs != null && nowMs - lastReportedAtMs > STALE_AFTER_MS;
  const effectiveNow = isActive && !isStale ? nowMs : (lastReportedAtMs ?? nowMs);

  const stepElapsedSeconds =
    stepStartedAtMs != null ? Math.max(0, (effectiveNow - stepStartedAtMs) / 1000) : 0;

  const plannedK = weights[k] || 0;
  const grace = Math.max(MIN_GRACE_SECONDS, GRACE_FRACTION * plannedK);
  const overrunSeconds = plannedK > 0 ? Math.max(0, stepElapsedSeconds - plannedK) : 0;
  const isOverrun = plannedK > 0 && overrunSeconds > grace;
  const fillPct = plannedK > 0 ? Math.min(100, (stepElapsedSeconds / plannedK) * 100) : 100;

  let overallPct = null;
  if (totalPlanned > 0) {
    const before = weights.slice(0, k).reduce((a, b) => a + b, 0);
    const currentContribution = Math.min(stepElapsedSeconds, plannedK);
    overallPct = ((before + currentContribution) / totalPlanned) * 100;
  }

  if (isActive) {
    const remainingBase = plannedK > 0 ? Math.max(0, plannedK - stepElapsedSeconds) : 0;
    const remainingAfter = weights.slice(k + 1).reduce((a, b) => a + b, 0);
    return {
      phase: "running",
      segments: baseSegments.map((s, i) => ({
        ...s,
        status: i < k ? "done" : i === k ? "current" : "upcoming",
        fillPct: i === k ? fillPct : i < k ? 100 : 0,
      })),
      overallPct: overallPct == null ? null : Math.min(99, overallPct),
      overrunSeconds,
      isOverrun,
      isStale,
      remainingSeconds: remainingBase + remainingAfter,
      remainingIsLowerBound: plannedK > 0 && stepElapsedSeconds >= plannedK,
      currentStepIndex: k,
      stepElapsedSeconds,
      stepPlannedSeconds: plannedK,
    };
  }

  // Ended (last_run): "completed" only when the last recorded step was the
  // final one AND an operator didn't explicitly cut it off there - an
  // explicit stop is the more informative fact to surface even in the
  // (rare) coincidence of stopping exactly on the last step.
  const isCompleted = k === n - 1 && !stoppedByOperator;
  return {
    phase: isCompleted ? "completed" : "ended-early",
    segments: baseSegments.map((s, i) => {
      if (isCompleted || i < k) return { ...s, status: "done", fillPct: 100 };
      if (i === k) return { ...s, status: "stopped", fillPct };
      return { ...s, status: "skipped", fillPct: 0 };
    }),
    overallPct: isCompleted ? 100 : overallPct == null ? null : Math.min(99, overallPct),
    stoppedByOperator,
    currentStepIndex: k,
    stepElapsedSeconds,
    stepPlannedSeconds: plannedK,
  };
}
